import { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'clsx';
import throttle from 'lodash/throttle';
import Motion from 'react-motion/lib/Motion';
import spring from 'react-motion/lib/spring';
import { forceCheck } from 'react-lazyload';
import ScrollReset from '../../shared/ScrollReset';
import { isXSmall } from '../../../utils/breakpoints';
import isBottomTitleVisible from '../../../utils/guideItem/isBottomTitleVisible';
import isDesktop from '../../../utils/desktop/isDesktop';
import CarouselItems from './CarouselItems';
import css from './gallery.module.scss';

const springConfig = {
  stiffness: 240,
  damping: 26,
};

export default function Carousel({
  showButtons,
  isLeftButtonDisabled,
  isRightButtonDisabled,
  handlePageLeft,
  handlePageRight,
  items,
  getStyles,
  numRows,
  page,
  breakpoint,
  isExtendedInfoEnabled,
}) {
  const firstGuideItem = items[0];
  const isOnDesktop = isDesktop();
  const addNoTitleStyle = !isBottomTitleVisible(firstGuideItem, getStyles);
  const motionStyle = useMemo(
    () => ({ pageOffset: spring(page, springConfig) }),
    [page],
  );
  // forces react-lazyload to re-attempt loading of elements in the viewport
  // applied below to ScrollReset for Mobile Web and to `pageOffset` updates for Desktop Web
  const forceCheckLazyLoad = useMemo(() => throttle(forceCheck, 100), []);

  return (
    <div className={css.relative} data-testid="containerGuideItemsContainer">
      {showButtons && (
        <div
          data-testid="containerLoadMoreLeftLink"
          onClick={handlePageLeft}
          className={classNames(css.pager, css.left, {
            [css.desktopPager]: isOnDesktop,
            [css.hide]: isLeftButtonDisabled,
            [css.noBottomTile]: addNoTitleStyle,
          })}
        >
          <div className={css.tableContainer}>
            <div className={css.chevronContainer}>
              <i
                className={classNames(css.chevronLeft, css.chevron)}
                disabled={isLeftButtonDisabled}
              />
            </div>
          </div>
        </div>
      )}
      <ScrollReset
        scrollHash={isXSmall(breakpoint)}
        className={css.container}
        onScroll={forceCheckLazyLoad}
      >
        <div className={css.pad}>
          <div
            data-testid="carouselContainer"
            className={css.carouselContainer}
          >
            <Motion defaultStyle={{ pageOffset: 0 }} style={motionStyle}>
              {({ pageOffset }) => {
                if (pageOffset > 0) {
                  forceCheckLazyLoad();
                }

                return (
                  <CarouselItems
                    items={items}
                    numRows={numRows}
                    getStyles={getStyles}
                    pageOffset={pageOffset}
                    breakpoint={breakpoint}
                    isExtendedInfoEnabled={isExtendedInfoEnabled}
                  />
                );
              }}
            </Motion>
          </div>
        </div>
      </ScrollReset>
      {showButtons && (
        <div
          data-testid="containerLoadMoreRightLink"
          onClick={handlePageRight}
          className={classNames(css.pager, css.right, {
            [css.desktopPager]: isOnDesktop,
            [css.hide]: isRightButtonDisabled,
            [css.noBottomTile]: addNoTitleStyle,
          })}
        >
          <div className={css.tableContainer}>
            <div className={css.chevronContainer}>
              <i
                className={classNames(css.chevronRight, css.chevron)}
                disabled={isRightButtonDisabled}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

Carousel.propTypes = {
  numRows: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  items: PropTypes.array.isRequired,
  breakpoint: PropTypes.number.isRequired,
  showButtons: PropTypes.bool.isRequired,
  isLeftButtonDisabled: PropTypes.bool.isRequired,
  isRightButtonDisabled: PropTypes.bool.isRequired,
  isExtendedInfoEnabled: PropTypes.bool,
  handlePageLeft: PropTypes.func.isRequired,
  handlePageRight: PropTypes.func.isRequired,
  getStyles: PropTypes.func,
};

Carousel.defaultProps = {
  getStyles: () => ({}),
  numRows: 1,
};
